import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThreadCard from '../components/ThreadCard';
import { getApiUrl } from '../config';

const Profile = () => {
  const { username } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [threads, setThreads] = useState([]);
  const [stats, setStats] = useState({ threads: 0, replies: 0, accepted: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('threads');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(getApiUrl(`/api/users/${username}`));
        if (response.ok) {
          const data = await response.json();
          setProfile(data.user);
          setStats(data.stats);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }

      try {
        const threadsResponse = await fetch(getApiUrl(`/api/users/${username}/threads`));
        if (threadsResponse.ok) {
          const data = await threadsResponse.json();
          setThreads(data.threads || []);
        }
      } catch (error) {
        console.error('Failed to fetch threads:', error);
      }

      setLoading(false);
    };
    fetchProfile();
  }, [username]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[1][0];
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatNumber = (num) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num;
  };

  if (loading) {
    return (
      <div className="main-container" style={{ display: 'block' }}>
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-tertiary)' }}>
          Loading profile...
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="main-container" style={{ display: 'block' }}>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <h2>User not found</h2>
          <Link to="/" style={{ color: 'var(--accent)', marginTop: '16px', display: 'inline-block' }}>
            ← Back to feed
          </Link>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser && currentUser.username === username;

  return (
    <div className="main-container" style={{ display: 'block', maxWidth: '900px' }}>
      {/* Profile Header */}
      <div style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '32px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
          <div style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--accent), var(--purple))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: '600',
            color: '#fff',
            flexShrink: 0
          }}>
            {getInitials(profile.username)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: '600' }}>{profile.username}</h1>
              {profile.isVerified && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--accent)">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              )}
              {profile.role && profile.role !== 'user' && (
                <span style={{
                  background: profile.role === 'expert' ? 'var(--warning-bg)' : 'var(--success-bg)',
                  color: profile.role === 'expert' ? 'var(--warning)' : 'var(--success)',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                </span>
              )}
            </div>
            <p style={{ color: 'var(--text-tertiary)', marginBottom: '12px' }}>@{profile.username}</p>
            {profile.bio && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: '1.6' }}>
                {profile.bio}
              </p>
            )}
            <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
              <div>
                <span style={{ fontWeight: '600', fontSize: '18px' }}>{formatNumber(profile.reputation || 0)}</span>
                <span style={{ color: 'var(--text-tertiary)', marginLeft: '6px', fontSize: '14px' }}>reputation</span>
              </div>
              <div>
                <span style={{ fontWeight: '600', fontSize: '18px' }}>{stats.threads}</span>
                <span style={{ color: 'var(--text-tertiary)', marginLeft: '6px', fontSize: '14px' }}>threads</span>
              </div>
              <div>
                <span style={{ fontWeight: '600', fontSize: '18px' }}>{stats.replies}</span>
                <span style={{ color: 'var(--text-tertiary)', marginLeft: '6px', fontSize: '14px' }}>replies</span>
              </div>
              <div>
                <span style={{ fontWeight: '600', fontSize: '18px' }}>{stats.accepted}</span>
                <span style={{ color: 'var(--text-tertiary)', marginLeft: '6px', fontSize: '14px' }}>accepted</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {profile.location && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-tertiary)', fontSize: '14px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {profile.location}
                </span>
              )}
              {profile.company && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-tertiary)', fontSize: '14px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  </svg>
                  {profile.company}
                </span>
              )}
              {profile.github && (
                <a href={`https://github.com/${profile.github}`} target="_blank" rel="noopener noreferrer" 
                   style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-tertiary)', fontSize: '14px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  {profile.github}
                </a>
              )}
            </div>
          </div>
          {isOwnProfile && (
            <button className="btn btn-ghost">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit Profile
            </button>
          )}
        </div>

        {/* Badges */}
        {profile.badges && profile.badges.length > 0 && (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Badges</h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {profile.badges.map((badge, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: badge.type === 'gold' ? '#fef3c7' : badge.type === 'silver' ? '#f3f4f6' : '#fef3e2',
                  borderRadius: '8px'
                }}>
                  <span style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: badge.type === 'gold' ? '#fbbf24' : badge.type === 'silver' ? '#9ca3af' : '#d97706'
                  }}></span>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills */}
        {profile.skills && profile.skills.length > 0 && (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Skills</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {profile.skills.map((skill, index) => (
                <span key={index} style={{
                  padding: '6px 12px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: 'var(--text-secondary)'
                }}>
                  {skill.name}
                  {skill.score > 0 && (
                    <span style={{ marginLeft: '6px', color: 'var(--accent)', fontWeight: '500' }}>
                      {skill.score}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content Tabs */}
      <div style={{ marginBottom: '24px' }}>
        <div className="feed-tabs" style={{ marginBottom: '20px' }}>
          <button 
            className={`feed-tab ${activeTab === 'threads' ? 'active' : ''}`}
            onClick={() => setActiveTab('threads')}
          >
            Threads
          </button>
          <button 
            className={`feed-tab ${activeTab === 'replies' ? 'active' : ''}`}
            onClick={() => setActiveTab('replies')}
          >
            Replies
          </button>
        </div>

        {activeTab === 'threads' && (
          threads.length > 0 ? (
            threads.map(thread => (
              <ThreadCard 
                key={thread._id} 
                thread={thread}
                showToast={() => {}}
                openAuthModal={() => {}}
              />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              No threads yet
            </div>
          )
        )}

        {activeTab === 'replies' && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
            Replies will appear here
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;

